import moment from 'moment'

import { omit } from 'common/utils/objectUtils'
import { intl } from 'common/components/l10n/IntlGlobalProvider'

// fail hard because dates are vital to the Tickets product
const validateR2RDateTime = (r2rDateTime, functionName) => {
  if (!(r2rDateTime instanceof R2RDateTime)) {
    throw new Error(`${functionName} of R2RDateTime expects an R2RDateTime`)
  }
}

const isDateValid = (date) => {
  const hasCorrectFormat = date.match(/^\d{4}-\d{2}-\d{2}$/) // will catch things like 2020/02/30
  const isRealDate = moment(date, 'YYYY-MM-DD').isValid()     // will catch things like 2020-30-02
  return hasCorrectFormat && isRealDate
}

const areHoursValid = (hours) => {
  const numberHours = Number(hours)
  return Number.isInteger(numberHours) && numberHours >= 0 && numberHours <= 23
}

const areMinutesValid = (minutes) => {
  const numberMinutes = Number(minutes)
  return Number.isInteger(numberMinutes) && numberMinutes >= 0 && numberMinutes <= 59
}

const isTimeValid = (time) => {
  const hasCorrectFormat = time.match(/^\d{2}:\d{2}$/)
  if (!hasCorrectFormat) {
    return false
  }
  const hours = time.split(':')[0]
  const minutes = time.split(':')[1]

  return areHoursValid(hours) && areMinutesValid(minutes)
}


class R2RDateTime {
  constructor(date, time) {
    // fail hard because dates and times are vital to the Tickets product
    if (!isDateValid(date)) {
      throw new Error(`R2RDateTime expects 'date' in format YYYY-MM-DD, got ${date}`)
    }
    if (!isTimeValid(time)) {
      throw new Error(`R2RDateTime expects 'time' in format HH:mm, got ${time}`)
    }

    this.date = date // format YYYY-MM-DD
    this.time = time // format HH:mm
  }

  getHour = () => Number(this.time.split(':')[0])

  toMomentUTC = () => moment.utc(`${this.date}T${this.time}Z`)

  // note: props are the same as react-intl's formatDate expects
  // https://github.com/yahoo/react-intl/wiki/API#formatdate
  formatAsString = (props) => {
    // note: normalized with UTC
    // it's very important that the timezone of "r2rDateTimeAsUTC" and the
    // "timezone" in formatProps match

    // this should be the ONLY use of intl's formatDate in the product.
    // timezones are easy to mess up, so keeping logic in as few places as possible
    const r2rDateTimeAsUTC = this.toMomentUTC()
    const formatProps = {
      ...omit(props, ['timeZone', 'value']),
      timeZone: 'utc',
    }

    return intl.formatDate(r2rDateTimeAsUTC, formatProps)
  }

  isAfter = (r2rDateTime, bufferMinutes = 0) => {
    validateR2RDateTime(r2rDateTime, 'isAfter')

    const thisMoment = this.toMomentUTC()
    const thatMoment = r2rDateTime.toMomentUTC().add(bufferMinutes, 'minutes')
    return thisMoment.isAfter(thatMoment)
  }

  isBefore = (r2rDateTime) => {
    validateR2RDateTime(r2rDateTime, 'isBefore')

    const thisMoment = this.toMomentUTC()
    const thatMoment = r2rDateTime.toMomentUTC()
    return thisMoment.isBefore(thatMoment)
  }

  isSameDate = (r2rDateTime) => {
    validateR2RDateTime(r2rDateTime, 'isSameDate')

    return this.date === r2rDateTime.date
  }

  dayDiff = (r2rDateTime) => {
    validateR2RDateTime(r2rDateTime, 'dayDiff')

    const thisDay = this.toMomentUTC().startOf('day')
    const thatDay = r2rDateTime.toMomentUTC().startOf('day')
    return thisDay.diff(thatDay, 'days')
  }

  // sometimes the only information we have is the number of hours
  // ex: time filters
  static fromHours(hours) {
    // validate
    if (!areHoursValid(hours)) {
      throw new Error(`R2RDateTime.fromHours expects an integer >= 0 and <= 23, got ${hours} `)
    }

    const date = moment().format('YYYY-MM-DD')

    const paddedHours = hours < 10 ? `0${hours}` : `${hours}`
    const time = `${paddedHours}:00`

    return new R2RDateTime(date, time)
  }

  // sometimes we are creating an R2RDateTime from just a date
  // ex: choosing a date in the search bar
  static fromDate(date) {
    // validate
    if (!isDateValid(date)) {
      throw new Error(`R2RDateTime.fromDate() expects 'date' in format YYYY-MM-DD, got ${date}`)
    }

    return new R2RDateTime(date, '00:00')
  }

  // sometimes we are creating an R2RDateTime from just a time
  // ex: timelines
  static fromTime(time) {
    // validate
    if (!isTimeValid(time)) {
      throw new Error(`R2RDateTime.fromDate() expects 'date' in format YYYY-MM-DD, got ${date}`)
    }

    const date = moment().format('YYYY-MM-DD')
    return new R2RDateTime(date, time)
  }

  // sometimes we're creating an R2RDateTime from an ISO string
  // ex: Checkout
  static fromISOString(isoString) {
    const isValid = moment(isoString, moment.ISO_8601, true).isValid()

    if (!isValid) {
      throw new Error(`R2RDateTime.fromISOString() expects ISO 8601 string, got ${isoString}`)
    }

    const date = isoString.split('T')[0]
    const matchedTime = isoString.match(/\d{2}:\d{2}/)
    const time = matchedTime ? matchedTime[0] : '00:00'

    return new R2RDateTime(date, time)
  }

  // todo: techdebt, remove need for this
  // the transfer form currently deals with moments
  static fromMomentUTC(momentObj) {
    // validate
    if (!momentObj.isUTC()) {
      throw new Error('R2RDateTime.fromMomentUTC() expects a moment with UTC time zone')
    }

    const date = momentObj.format('YYYY-MM-DD')
    const time = momentObj.format('HH:mm')
    return new R2RDateTime(date, time)
  }
}

export default R2RDateTime
